import { AlertCircle, RotateCcw } from 'lucide-react'
import * as React from 'react'
import type { TranscriptUtterance } from '@/client/types.gen'
import { TranscriptAudioQualityNudge } from '@/components/project/artifact-viewer/transcript/TranscriptAudioQualityNudge'
import { TranscriptSkeleton } from '@/components/project/artifact-viewer/transcript/TranscriptSkeleton'
import { TranscriptUtteranceRow } from '@/components/project/artifact-viewer/transcript/TranscriptUtteranceRow'
import {
	getColorForSpeaker,
	renderHighlightedText,
} from '@/components/project/artifact-viewer/transcript/utils'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/hooks/useTranslation'

export interface TranscriptPanelProps {
	/** Plain text content (fallback when no utterances) */
	content: string
	loading: boolean
	error: string | null
	onRetry: () => Promise<void>
	/** Edited utterances map (index -> edited text) */
	editedUtterances: Map<number, string>
	/** Edited speakers map (index -> edited speaker name) */
	editedSpeakers?: Map<number, string>
	/** Callback when utterance is edited */
	onUtteranceEdit: (index: number, text: string) => void
	/** Transcript status */
	status: string
	/** For find highlighting */
	matchRanges?: Array<{ start: number; end: number }>
	currentMatchIndex?: number
	/** Full utterance index containing the current find match (for scroll-into-view); -1 if none */
	currentMatchUtteranceIndex?: number
	/** Structured utterances with timestamps */
	utterances?: TranscriptUtterance[] | null
	/** Selected speakers for filtering */
	selectedSpeakers?: Set<string>
	/** Active utterance index (for audio sync highlighting) */
	activeUtteranceIndex?: number
	/** Callback when timestamp is clicked (seek to time) */
	onSeekTo?: (timeMs: number) => void
	/** List of speakers for the legend */
	speakers?: string[]
	/** Low-confidence audio quality nudge (above transcript body) */
	showAudioQualityNudge?: boolean
	onDismissAudioQualityNudge?: () => void
	reserveAudioQualityNudgeSlot?: boolean
}

export function TranscriptPanel({
	content,
	loading,
	error,
	onRetry,
	editedUtterances,
	editedSpeakers = new Map(),
	onUtteranceEdit,
	status,
	matchRanges = [],
	currentMatchIndex = 0,
	currentMatchUtteranceIndex = -1,
	utterances = null,
	selectedSpeakers,
	activeUtteranceIndex = -1,
	onSeekTo,
	speakers = [],
	showAudioQualityNudge = false,
	onDismissAudioQualityNudge,
	reserveAudioQualityNudgeSlot = false,
}: TranscriptPanelProps) {
	const { t } = useTranslation()
	const textareaRefs = React.useRef<Map<number, HTMLTextAreaElement>>(new Map())
	const rowRefs = React.useRef<Map<number, HTMLDivElement>>(new Map())
	const plainTextContainerRef = React.useRef<HTMLDivElement>(null)

	const handleTextareaChange = React.useCallback(
		(index: number, value: string, textarea: HTMLTextAreaElement | null) => {
			onUtteranceEdit(index, value)
			if (textarea) {
				textarea.style.height = 'auto'
				textarea.style.height = `${textarea.scrollHeight}px`
			}
		},
		[onUtteranceEdit]
	)

	const registerTextareaRef = React.useCallback((index: number, el: HTMLTextAreaElement | null) => {
		if (el) {
			textareaRefs.current.set(index, el)
			el.style.height = 'auto'
			el.style.height = `${el.scrollHeight}px`
		} else {
			textareaRefs.current.delete(index)
		}
	}, [])

	const getOriginalIndex = React.useCallback(
		(utterance: TranscriptUtterance): number => {
			if (!utterances) return 0
			return utterances.indexOf(utterance)
		},
		[utterances]
	)

	// Re-run when utterance list or edited text changes to resize textareas
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional – resize when utterances/edits change
	React.useLayoutEffect(() => {
		textareaRefs.current.forEach((textarea) => {
			if (textarea) {
				textarea.style.height = 'auto'
				textarea.style.height = `${textarea.scrollHeight}px`
			}
		})
	}, [utterances, editedUtterances])

	// Scroll to current find match in utterance mode
	React.useLayoutEffect(() => {
		if (currentMatchUtteranceIndex < 0 || matchRanges.length === 0) return
		rowRefs.current.get(currentMatchUtteranceIndex)?.scrollIntoView({
			block: 'nearest',
			behavior: 'smooth',
		})
	}, [currentMatchUtteranceIndex, matchRanges.length])

	// Scroll to current find match in plain-text mode
	// biome-ignore lint/correctness/useExhaustiveDependencies: scroll when the active match index changes
	React.useLayoutEffect(() => {
		if (matchRanges.length === 0) return
		plainTextContainerRef.current
			?.querySelector<HTMLElement>('[data-current-match="true"]')
			?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
	}, [currentMatchIndex, matchRanges.length])

	// Keep the active utterance in view while audio plays (index changes at utterance boundaries)
	React.useLayoutEffect(() => {
		if (!utterances?.length || activeUtteranceIndex < 0) return
		const rowEl = rowRefs.current.get(activeUtteranceIndex)
		if (!rowEl) return
		const reduceMotion =
			typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
		rowEl.scrollIntoView({
			block: 'center',
			behavior: reduceMotion ? 'auto' : 'smooth',
		})
	}, [activeUtteranceIndex, utterances])

	const getCurrentSpeaker = React.useCallback(
		(utterance: TranscriptUtterance, index: number): string => {
			return editedSpeakers.get(index) ?? utterance.speaker
		},
		[editedSpeakers]
	)

	const isProcessing = status === 'processing'
	const showPartialMessage = isProcessing && content.length > 0
	const useUtterances = utterances && utterances.length > 0
	const filteredUtterances = useUtterances
		? utterances.filter((u, idx) => {
				const currentSpeaker = getCurrentSpeaker(u, idx)
				return !selectedSpeakers || selectedSpeakers.size === 0 || selectedSpeakers.has(currentSpeaker)
			})
		: []

	// Per-row find highlight: local ranges and which is current (for utterance-mode highlighting)
	const matchHighlightByRowIndex = React.useMemo(() => {
		if (matchRanges.length === 0 || filteredUtterances.length === 0) return []
		let textOffset = 0
		const byRow: Array<{
			localRanges: Array<{ start: number; end: number }>
			currentLocalIndex: number
		}> = filteredUtterances.map(() => ({ localRanges: [], currentLocalIndex: -1 }))
		for (let rowIdx = 0; rowIdx < filteredUtterances.length; rowIdx++) {
			const utterance = filteredUtterances[rowIdx]
			const originalIdx = getOriginalIndex(utterance)
			const editedText = editedUtterances.get(originalIdx) ?? utterance.text
			const currentSpeaker = getCurrentSpeaker(utterance, originalIdx)
			const prefixLength = currentSpeaker.length + 2
			const textLength = editedText.length
			const lineStart = textOffset + prefixLength
			const lineEnd = lineStart + textLength

			for (let gi = 0; gi < matchRanges.length; gi++) {
				const r = matchRanges[gi]
				if (r.start < lineEnd && r.end > lineStart) {
					const localStart = Math.max(0, r.start - lineStart)
					const localEnd = Math.min(textLength, r.end - lineStart)
					if (localEnd > localStart) {
						byRow[rowIdx].localRanges.push({ start: localStart, end: localEnd })
						if (gi === currentMatchIndex)
							byRow[rowIdx].currentLocalIndex = byRow[rowIdx].localRanges.length - 1
					}
				}
			}
			textOffset += prefixLength + textLength + 1
		}
		return byRow
	}, [
		matchRanges,
		currentMatchIndex,
		filteredUtterances,
		editedUtterances,
		getOriginalIndex,
		getCurrentSpeaker,
	])

	if (loading && !content) {
		return <TranscriptSkeleton />
	}

	if (error && !loading) {
		return (
			<div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4" role="alert">
				<div className="flex items-start gap-3">
					<AlertCircle className="size-5 shrink-0 text-destructive mt-0.5" aria-hidden />
					<div className="flex-1 min-w-0">
						<h3 className="font-semibold text-destructive mb-1">
							{t('pages.projectDetail.artifactPreview.errorLoading')}
						</h3>
						<p className="text-sm text-destructive mb-4">{error}</p>
						<Button
							variant="outline"
							size="sm"
							onClick={() => onRetry()}
							className="border-destructive/50 text-destructive hover:bg-destructive/10"
						>
							<RotateCcw className="size-3.5 mr-1.5" aria-hidden />
							{t('pages.projectDetail.artifactPreview.retry')}
						</Button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="bg-background">
			<div className="mx-auto max-w-[700px] px-4 pt-2 pb-16 md:px-6 md:pb-20">
				{reserveAudioQualityNudgeSlot && onDismissAudioQualityNudge && (
					<TranscriptAudioQualityNudge
						visible={showAudioQualityNudge}
						onDismiss={onDismissAudioQualityNudge}
					/>
				)}
				{speakers.length > 0 && (
					<div className="flex flex-wrap gap-3 mb-4">
						{speakers.map((speaker) => (
							<div key={speaker} className="flex items-center gap-1.5">
								<div
									className="w-2.5 h-2.5 rounded-sm"
									style={{ backgroundColor: getColorForSpeaker(speaker) }}
								/>
								<span className="text-sm text-muted-foreground">{speaker}</span>
							</div>
						))}
					</div>
				)}

				{showPartialMessage && (
					<p className="text-sm text-muted-foreground mb-4">
						{t('pages.projectDetail.transcript.partialTranscript')}
					</p>
				)}

				{useUtterances && filteredUtterances.length > 0 ? (
					<article className="leading-relaxed">
						{filteredUtterances.map((utterance, rowIdx) => {
							const originalIdx = getOriginalIndex(utterance)
							const editedText = editedUtterances.get(originalIdx) ?? utterance.text
							const currentSpeaker = getCurrentSpeaker(utterance, originalIdx)
							const speakerColor = getColorForSpeaker(currentSpeaker)
							const isActive = originalIdx === activeUtteranceIndex
							const highlight = matchHighlightByRowIndex[rowIdx]
							const localMatchRanges = highlight?.localRanges ?? []
							const currentLocalMatchIndex = highlight?.currentLocalIndex ?? -1
							return (
								<div
									key={`u-${utterance.start}-${utterance.end}-${originalIdx}`}
									ref={(el) => {
										if (el) rowRefs.current.set(originalIdx, el)
										else rowRefs.current.delete(originalIdx)
									}}
								>
									<TranscriptUtteranceRow
										utterance={utterance}
										originalIdx={originalIdx}
										editedText={editedText}
										currentSpeaker={currentSpeaker}
										speakerColor={speakerColor}
										isActive={isActive}
										onSeekTo={onSeekTo}
										onTextChange={handleTextareaChange}
										registerTextareaRef={registerTextareaRef}
										localMatchRanges={localMatchRanges}
										currentLocalMatchIndex={currentLocalMatchIndex}
									/>
								</div>
							)
						})}
					</article>
				) : (
					<div ref={plainTextContainerRef} className="rounded-md bg-muted/30 min-h-[400px] p-4">
						{content ? (
							<pre className="whitespace-pre-wrap font-sans text-sm text-foreground break-words">
								{matchRanges.length > 0
									? renderHighlightedText(content, matchRanges, currentMatchIndex)
									: content}
							</pre>
						) : (
							<p className="text-sm text-muted-foreground">
								{t('pages.projectDetail.transcript.noTranscript')}
							</p>
						)}
					</div>
				)}
			</div>
		</div>
	)
}
